import React, { useState, useEffect, useMemo } from 'react';
import { Card, Select, FormLayout, Popover, Button, TextField, Text, InlineStack, Checkbox } from '@shopify/polaris';
import { LineChart, Line, ResponsiveContainer, CartesianGrid, XAxis, YAxis, Tooltip, Legend, ReferenceLine, ReferenceDot } from 'recharts';
import { formatDate, formatToCurrency } from '../../../../shared/components/functions';
import { useToggle } from '../../../../shared/useToogle';
import { CardHeading } from '../../../../shared/components/cards/CardHeading';
import { formatAnalyticsPeriodLabel } from './periodFormatting';

const PROJECTED_KEYS = ['bruto_marge_tax_excl', 'invoices_out_corrected_tax_excl', 'invoices_in_corrected_tax_excl'];

function getPeriodYear(point) {
  const value = point?.period ?? point?.name;
  if (value === null || value === undefined) return null;
  const year = Number(String(value).slice(0, 4));
  return Number.isFinite(year) ? year : null;
}

function toDateOnly(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function getDayOfYear(date) {
  const yearStart = new Date(date.getFullYear(), 0, 1);
  return Math.floor((date - yearStart) / (24 * 60 * 60 * 1000)) + 1;
}

function getDaysInYear(year) {
  const isLeapYear = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
  return isLeapYear ? 366 : 365;
}

function toNumeric(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : NaN;
  if (typeof value !== 'string') return NaN;

  const trimmed = value.trim();
  if (!trimmed) return NaN;

  let normalized = trimmed;
  if (trimmed.includes(',') && trimmed.includes('.')) {
    normalized = trimmed.replace(/,/g, '');
  } else if (trimmed.includes(',') && !trimmed.includes('.')) {
    normalized = trimmed.replace(/,/g, '.');
  }
  normalized = normalized.replace(/[^\d.-]/g, '');

  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : NaN;
}

const lineConfigs = [
  {
    key: 'bruto_marge_tax_excl',
    label: 'Gross Margin (excl. tax)',
    color: '#1E7D22', // dark green
    defaultVisible: true
  },
  {
    key: 'invoices_out_corrected_tax_excl',
    label: 'Invoices Out Corrected (excl. tax)',
    color: '#2ECC40', // green (outgoing)
    defaultVisible: true
  },
  {
    key: 'invoices_in_corrected_tax_excl',
    label: 'Invoices In Corrected (excl. tax)',
    color: '#B22222', // firebrick red (incoming)
    defaultVisible: true
  },
  {
    key: 'invoices_out_tax_excl',
    label: 'Invoices Out (excl. tax)',
    color: '#3D9970', // green (outgoing)
    defaultVisible: false
  },
  {
    key: 'invoices_out_tax_incl',
    label: 'Invoices Out (incl. tax)',
    color: '#01FF70', // light green (outgoing)
    defaultVisible: false
  },
  {
    key: 'output_tax',
    label: 'Output Tax',
    color: '#FF851B', // orange
    defaultVisible: false
  },
  {
    key: 'invoices_in_tax_excl',
    label: 'Invoices In (excl. tax)',
    color: '#FF4136', // red (incoming)
    defaultVisible: false
  },
  {
    key: 'invoices_in_tax_included',
    label: 'Invoices In (incl. tax)',
    color: '#FF6347', // tomato (incoming)
    defaultVisible: false
  },
  {
    key: 'input_tax',
    label: 'Input Tax',
    color: '#7FDBFF', // blue
    defaultVisible: false
  },
  {
    key: 'payable_tax',
    label: 'Payable Tax',
    color: '#B10DC9', // purple
    defaultVisible: false
  },
  {
    key: 'creditnotes_out_tax_excluded',
    label: 'Creditnotes Out (excl. tax)',
    color: '#2ECC40', // green (outgoing)
    defaultVisible: false
  },
  {
    key: 'creditnotes_in_tax_excluded',
    label: 'Creditnotes In (excl. tax)',
    color: '#B22222', // red (incoming)
    defaultVisible: false
  }
];

export default function BrutoProfitGraph() {
  const [data, setData] = useState([]);
  const [groupBy, setGroupBy] = useState('YEAR');
  const [popoverActive, setPopoverActive] = useToggle();
  const [startDate, setStartDate] = useState(formatDate('1996-01-01'));
  const [endDate, setEndDate] = useState(formatDate(new Date()));
  const [visibleLines, setVisibleLines] = useState(() => {
    const initial = {};
    lineConfigs.forEach((cfg) => {
      initial[cfg.key] = !!cfg.defaultVisible;
    });
    return initial;
  });

  const activator = (
    <Button onClick={setPopoverActive} disclosure>
      Customize
    </Button>
  );

  const options = [
    { label: 'Month', value: 'MONTH' },
    { label: 'Quarter', value: 'QUARTER' },
    { label: 'Year', value: 'YEAR' }
  ];

  const handleLineToggle = (key) => {
    setVisibleLines((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  async function fetchData() {
    const response = await fetch(`/api/metrics/bruto_profit?groupBy=${groupBy}&startDate=${startDate}&endDate=${endDate}`);
    const data = await response.json();
    setData(
      data.map((point) => {
        return {
          name: point.period,
          ...point
        };
      })
    );
  }

  useEffect(() => {
    fetchData();
  }, [groupBy, startDate, endDate]);

  const projectionInfo = useMemo(() => {
    if (groupBy !== 'YEAR' || !Array.isArray(data) || data.length === 0) {
      return null;
    }

    const todayDateOnly = toDateOnly(new Date());
    const currentYear = todayDateOnly.getFullYear();
    const yearEntries = data.map((point, idx) => ({ idx, year: getPeriodYear(point) })).filter((entry) => Number.isFinite(entry.year));
    if (yearEntries.length === 0) {
      return null;
    }
    const currentYearEntry = yearEntries.find((entry) => entry.year === currentYear);
    const pastOrCurrentEntries = yearEntries.filter((entry) => entry.year <= currentYear);
    const fallbackEntry = pastOrCurrentEntries[pastOrCurrentEntries.length - 1] || yearEntries[yearEntries.length - 1];
    const targetEntry = currentYearEntry || fallbackEntry;
    const targetYearIndex = targetEntry?.idx;
    if (targetYearIndex === undefined) return null;

    const targetPoint = data[targetYearIndex];
    const targetYear = targetEntry.year;
    const targetYearDays = getDaysInYear(targetYear);
    let projectionReferenceDate = todayDateOnly;
    if (targetYear !== currentYear) {
      projectionReferenceDate = new Date(targetYear, todayDateOnly.getMonth(), todayDateOnly.getDate());
      if (projectionReferenceDate.getMonth() !== todayDateOnly.getMonth()) {
        projectionReferenceDate = new Date(targetYear, todayDateOnly.getMonth() + 1, 0);
      }
      const yearEnd = new Date(targetYear, 11, 31);
      if (projectionReferenceDate > yearEnd) {
        projectionReferenceDate = yearEnd;
      }
    }
    let elapsedDays = getDayOfYear(projectionReferenceDate);
    elapsedDays = Math.max(1, Math.min(elapsedDays, targetYearDays - 1));

    const runRateFactor = targetYearDays / elapsedDays;
    const currentValues = {};
    const projectedValues = {};
    PROJECTED_KEYS.forEach((key) => {
      const currentValue = toNumeric(targetPoint[key]);
      if (!Number.isFinite(currentValue)) return;
      currentValues[key] = currentValue;
      projectedValues[key] = currentValue * runRateFactor;
    });

    const hasProjectedValues = Object.keys(projectedValues).length > 0;
    if (!hasProjectedValues) {
      return null;
    }

    const previousPoint = targetYearIndex > 0 ? data[targetYearIndex - 1] : null;
    const previousValues = {};
    PROJECTED_KEYS.forEach((key) => {
      previousValues[key] = toNumeric(previousPoint?.[key]);
    });

    return {
      targetLabel: targetPoint?.name,
      previousLabel: previousPoint?.name,
      currentValues,
      previousValues,
      projectedValues
    };
  }, [data, groupBy]);

  const customize_graph = (
    <Popover active={popoverActive} activator={activator} onClose={setPopoverActive} ariaHaspopup={false} sectioned>
      <FormLayout>
        <Select label="Group by" options={options} onChange={setGroupBy} value={groupBy} />
        <TextField id="start_date" maxLength={60} type="date" autoComplete="off" label="Start Date" value={startDate} onChange={setStartDate} />
        <TextField id="end_date" maxLength={60} type="date" autoComplete="off" label="End Date" value={endDate} onChange={setEndDate} />
        <div style={{ marginTop: 12 }}>
          <Text variant="headingSm" as="h6">
            Show/Hide Curves
          </Text>
          {lineConfigs.map((cfg) => (
            <Checkbox key={cfg.key} label={cfg.label} checked={visibleLines[cfg.key]} onChange={() => handleLineToggle(cfg.key)} />
          ))}
        </div>
      </FormLayout>
    </Popover>
  );

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const visiblePayload = payload.filter((entry) => Number.isFinite(entry?.value));
      const showProjection = groupBy === 'YEAR' && projectionInfo && label === projectionInfo.targetLabel;
      const projectedTooltipEntries = showProjection
        ? lineConfigs
            .filter((cfg) => PROJECTED_KEYS.includes(cfg.key) && visibleLines[cfg.key])
            .map((cfg) => {
              const projectedValue = projectionInfo.projectedValues[cfg.key];
              if (!Number.isFinite(projectedValue)) return null;
              return {
                key: cfg.key,
                label: cfg.label,
                value: projectedValue
              };
            })
            .filter(Boolean)
        : [];

      return (
        <Card>
          <Text className="label">{groupBy === 'YEAR' ? `Year: ${label}` : `Period: ${formatAnalyticsPeriodLabel(label)}`}</Text>
          {visiblePayload.map((entry, idx) => (
            <p className="label" key={idx}>
              {`${entry.name || entry.dataKey} : ${formatToCurrency(entry.value)}`}
            </p>
          ))}
          {projectedTooltipEntries.length > 0 ? <div style={{ borderTop: '1px solid #E1E3E5', margin: '8px 0' }} /> : null}
          {projectedTooltipEntries.map((entry) => (
            <p className="label" key={`projected-${entry.key}`}>
              {`${entry.label} (Projected EOY) : ${formatToCurrency(entry.value)}`}
            </p>
          ))}
        </Card>
      );
    }
    return null;
  };

  return (
    <Card>
      {/* Legend for financial metrics */}
      <ul style={{ margin: 0, paddingLeft: 20, fontSize: 14 }}>
        <li>
          <b>invoices_out</b>: totaal per jaar facturen uit <b>excl. btw</b>
        </li>
        <li>
          <b>invoices_in</b>: totaal per jaar facturen in <b>excl. geschatte btw</b> <i>(er wordt geen btw geregistreerd op inkomende facturen)</i>
        </li>
        <li>
          <b>creditnotes_out</b>: totaal per jaar uitgaande credit notes excl. btw
        </li>
        <li>
          <b>creditnotes_in</b>: totaal per jaar inkomende credit notes excl. geschatte btw
        </li>
        <li>
          <b>invoices_out_corrected</b>: uitgaande facturen excl. btw gecorrigeerd met uitgaande creditnotes
        </li>
        <li>
          <b>invoices_in_corrected</b>: inkomende facturen excl. btw gecorrigeerd met inkomende creditnotes
        </li>
        <li>
          <b>bruto_marge</b>: gecorrigeerde uitgaande facturen minus gecorrigeerde inkomende facturen, excl. btw
        </li>
        <li>
          <b>output_tax</b>: alle btw die we heffen op uitgaande facturen (uitgaande btw)
        </li>
        <li>
          <b>input_tax</b>: alle btw die we betalen op inkomende facturen (inkomende btw)
        </li>
        <li>
          <b>payable_tax</b>: alle vorderbare btw, oftewel input_tax - output_tax
        </li>
      </ul>
      <InlineStack align="space-between">
        <CardHeading />
        {customize_graph}
      </InlineStack>

      <div style={{ width: '100%', height: '650px', marginTop: '20px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            width={1500}
            height={300}
            data={data}
            margin={{
              top: 5,
              right: 30,
              left: 20,
              bottom: 5
            }}
          >
            <Legend />
            <CartesianGrid strokeDasharray="1 5" />
            <XAxis dataKey="name" tickFormatter={formatAnalyticsPeriodLabel} />
            <YAxis />
            <Tooltip content={<CustomTooltip />} />
            {lineConfigs.map((cfg) => (visibleLines[cfg.key] ? <Line key={cfg.key} type="monotone" dataKey={cfg.key} stroke={cfg.color} activeDot={{ r: 8 }} name={cfg.label} /> : null))}
            {projectionInfo &&
              lineConfigs
                .filter((cfg) => PROJECTED_KEYS.includes(cfg.key) && visibleLines[cfg.key])
                .map((cfg) => {
                  const currentValue = projectionInfo.currentValues[cfg.key];
                  const previousValue = projectionInfo.previousValues[cfg.key];
                  const projectedValue = projectionInfo.projectedValues[cfg.key];
                  if (!Number.isFinite(currentValue) || !Number.isFinite(projectedValue)) return null;

                  const segmentStartX = projectionInfo.previousLabel ?? projectionInfo.targetLabel;
                  const segmentStartY = Number.isFinite(previousValue) ? previousValue : currentValue;

                  return (
                    <React.Fragment key={`${cfg.key}__projection`}>
                      <ReferenceLine
                        ifOverflow="extendDomain"
                        segment={[
                          { x: segmentStartX, y: segmentStartY },
                          { x: projectionInfo.targetLabel, y: projectedValue }
                        ]}
                        stroke={cfg.color}
                        strokeDasharray="6 6"
                        strokeWidth={2}
                      />
                      <ReferenceDot x={projectionInfo.targetLabel} y={projectedValue} r={5} fill={cfg.color} stroke="white" ifOverflow="extendDomain" />
                    </React.Fragment>
                  );
                })}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}
